/* import __COLOCATED_TEMPLATE__ from './specifications-magnitude.hbs'; */
import Component from '@glimmer/component';
import FilterConstants from '../../filter-constants';
import SpecificationResult from 'filter-wizard/models/results/specification';
import * as Format from '../../utils/format';
import * as Conversion from '../../models/utils/conversion';

const Position = { LEFT: 'Left', CENTER: 'Center', RIGHT: 'Right' };
const LEFTTOPADJUST = 1;
const RIGHTBOTTOMADJUST = 2;

interface SpecMagnitudeArgs {
  geometry: {
    width: number;
    height: number;
    limits: { [key: string]: number };
    xScale: (arg0: number) => number;
    yScale: (arg0: number) => number;
  };
  model: SpecificationResult;
  figureName: string;
}

type BandPassStopBandFrequency = {
  stop: number;
  start: number;
};

type ResponseTextPos = {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
};

type PassbandRegion = {
  x: number;
  y: number;
  w: number;
  h: number;
};

type PassbandRegionLabelPos = {
  x: number;
  y: number;
};

type ArrowPosition = {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
};

type PassbandAttenuationLabel = {
  value: string;
  x: number;
  y: number;
};

type PassbandFrequencyLabel = {
  value: string;
  x: number;
  y: number;
  anchor: string;
};

type StopbandRegion = {
  x1: number;
  y1: number;
  w1: number;
  h1: number;
  x2: number;
  y2: number;
  w2: number;
  h2: number;
};

type StopbandRegionLabelPos = {
  x1: number;
  y1: number;
  anchor: string;
  x2: number;
  y2: number;
};

type StopbandAttenuationLabel = {
  value: string;
  x1: number;
  y1: number;
  anchor: string;
  x2: number;
  y2: number;
};

type StopbandFrequencyLabel = {
  value: string;
  x: number;
  y: number;
  anchor: string;
};

type CenterFrequencyLabel = {
  value: string;
  x: number;
  y: number;
};

type StopbandStopStartFrequencyLabel = {
  stop: {
    value: string;
    x: number;
    y: number;
  };
  start: {
    value: string;
    x: number;
    y: number;
  };
};

type GainValueLabel = {
  value: string;
  x: number;
  y: number;
  anchor: string;
};

type PassbandRipple = {
  label: {
    x: number;
    y: number;
    anchor: string;
  };
  rippleTop: {
    x1: number;
    y1: number;
    x2: number;
    y2: number;
  };
  rippleBottom: {
    x1: number;
    y1: number;
    x2: number;
    y2: number;
  };
};

export default class SpecMagnitudeAnnotation extends Component<SpecMagnitudeArgs> {
  // Gain in dB
  get gainDB(): number {
    const gainUnit = this.args.model.specificationsInput.gainUnit;
    const gain = this.args.model.specificationsInput.gain;
    let result = gain;
    if (gainUnit === FilterConstants.gainUnits.vv) {
      result = Conversion.VVtoDB(gain);
    }
    return result;
  }

  // Stopband Stop and Start Frequency (only for Band Pass)
  get bandpassStopBandFrequency(): BandPassStopBandFrequency {
    let stop = 0,
      start = 0;
    const filterType = this.args.model.filterName;
    if (filterType === FilterConstants.filterType.bandPass) {
      const stopband = this.args.model.specificationsInput.stopbandFrequency;
      const center = this.args.model.specificationsInput.centerFrequency;
      stop = (Math.sqrt(stopband * stopband + 4 * center * center) - stopband) / 2;
      start = stop + stopband;
    }
    return { stop: stop, start: start };
  }

  // Flag that determines if the Filter Type is Band Pass
  get isBandpassFilterType(): boolean {
    return this.args.model.filter.name === FilterConstants.filterType.bandPass;
  }

  // Flag that determines if the Filter Response is Chebyshev
  get isChebyshevFilterResponse(): boolean {
    return this.args.model.responseType === FilterConstants.responseType.chebyshev;
  }

  // Filter Response Text - Contains  Filter Nth Order, Filter Response, Passband Ripple and Number of Stages
  get responseTextPos(): ResponseTextPos {
    {
      let x1 = 0,
        y1 = 0,
        x2 = 0,
        y2 = 0;
      const position = this.getResponsePosition();
      switch (position) {
        case Position.LEFT:
          x1 = x2 = 10;
          y1 = this.args.geometry.height - 30;
          y2 = this.args.geometry.height - 10;
          break;
        case Position.RIGHT:
          x1 = x2 = this.args.geometry.width - 200;
          y1 = this.args.geometry.height - 30;
          y2 = this.args.geometry.height - 10;
          break;
        case Position.CENTER:
          x1 = x2 = Math.round(this.args.geometry.width / 2) - 80;
          y1 = this.args.geometry.height - 30;
          y2 = this.args.geometry.height - 10;
          break;
      }
      return { x1: x1, y1: y1, x2: x2, y2: y2 };
    }
  }

  // Passband Region Area
  get passbandRegion(): PassbandRegion {
    let x = 0,
      y = 0,
      w = 0,
      h = 0; // Passband Region Area
    const filterType = this.args.model.filterName;

    if (this.args.geometry.limits['xmin'] && this.args.geometry.limits['xmax'] && this.args.geometry.limits['ymax']) {
      // Y position and height is consistent for all filter types for Passband Region
      y = this.yScale(this.args.geometry.limits['ymax']) + LEFTTOPADJUST;
      h = y + this.yScale(this.args.model.specificationsInput.passbandAttenuation + this.gainDB) - RIGHTBOTTOMADJUST;

      switch (filterType) {
        case FilterConstants.filterType.lowPass: {
          // Passband Region is Top Left for Low Pass Filter
          x = this.xScale(this.args.geometry.limits['xmin']) + LEFTTOPADJUST;
          w = this.xScale(this.args.model.specificationsInput.passbandFrequency) - x - RIGHTBOTTOMADJUST;
          break;
        }
        case FilterConstants.filterType.highPass: {
          // Passband Region is Top Right for High Pass Filter
          x = this.xScale(this.args.model.specificationsInput.passbandFrequency) + LEFTTOPADJUST;
          w = this.xScale(this.args.geometry.limits['xmax']) - x - RIGHTBOTTOMADJUST;
          break;
        }
        case FilterConstants.filterType.bandPass: {
          // Passband Region is Top Center for Band Pass Filter
          const passband = this.args.model.specificationsInput.passbandFrequency;
          const center = this.args.model.specificationsInput.centerFrequency;
          const start = (Math.sqrt(passband * passband + 4 * center * center) - passband) / 2;
          const stop = start + passband;
          x = this.xScale(start) + LEFTTOPADJUST;
          w = this.xScale(stop) - x - RIGHTBOTTOMADJUST;
          break;
        }
      }
    }
    return { x: x, y: y, w: w, h: h };
  }

  // Passband Region Text Position
  get passbandRegionLabelPos(): PassbandRegionLabelPos {
    const area = this.passbandRegion;
    const x = area.x + area.w / 2;
    const y = area.y + 10;
    return { x: x, y: y };
  }

  // Passband Arrows Position (only for Band Pass)
  get passbandArrows(): ArrowPosition {
    const area = this.passbandRegion;
    const x1 = area.x;
    const x2 = area.x + area.w;
    const y1 = area.y + 20;
    const y2 = y1;
    return { x1: x1, y1: y1, x2: x2, y2: y2 };
  }

  // Passband Attenuation Text Position
  get passbandAttenuationLabel(): PassbandAttenuationLabel {
    const value = Format.formatFieldValue(
      this.args.model.specificationsInput.passbandAttenuation,
      FilterConstants.fieldTypes.NumericDecimal,
      '3s',
    );
    const area = this.passbandRegion;
    const x = area.x + area.w / 2;
    const y = area.y + area.h + 12;
    return { value: value, x: x, y: y };
  }

  // Passband Frequency Text Position
  get passbandFrequencyLabel(): PassbandFrequencyLabel {
    const area = this.passbandRegion;
    const filterType = this.args.model.filterName;
    const gainLine = this.yScale(Number(this.gainDB));
    const value = Format.formatFieldValue(
      this.args.model.specificationsInput.passbandFrequency,
      FilterConstants.fieldTypes.Engineering,
      '3s',
    );
    let x = 0,
      y = 0;
    let anchor = 'text-anchor-start';
    switch (filterType) {
      case FilterConstants.filterType.lowPass:
        // Frequency Position is Right of Passband Region for Low Pass Filter
        x = area.x + area.w + 5;
        y = area.y + area.h / 2;
        if (y >= gainLine - 15 && y <= gainLine + 15) y = gainLine - 15;
        anchor = 'text-anchor-start';
        break;
      case FilterConstants.filterType.highPass:
        // Frequency Position is Left of Passband Region for High Pass Filter
        x = area.x - 5;
        y = area.y + area.h / 2;
        if (y >= gainLine - 15 && y <= gainLine + 15) y = gainLine - 15;
        anchor = 'text-anchor-end';
        break;
      case FilterConstants.filterType.bandPass:
        // Frequency Position is Right of Passband Region for Band Pass Filter
        x = area.x + area.w + 5;
        y = area.y + area.h / 2;
        if (y >= gainLine - 15 && y <= gainLine + 15) y = gainLine - 15;
        anchor = 'text-anchor-start';
        break;
    }
    return { value: value, x: x, y: y, anchor: anchor };
  }

  // Stopband Region Areas (There are two for Bandpass Filters)
  get stopbandRegion(): StopbandRegion {
    let x1 = 0,
      y1 = 0,
      w1 = 0,
      h1 = 0; // Passband Region Area (1)
    let x2 = 0,
      y2 = 0,
      w2 = 0,
      h2 = 0; // Passband Region Area (2)

    if (this.args.geometry.limits['xmin'] && this.args.geometry.limits['xmax'] && this.args.geometry.limits['ymin']) {
      // Y position and height is consistent for all filter types for Stopband Region
      y1 = this.yScale(this.args.model.specificationsInput.stopbandAttenuation + this.gainDB) + LEFTTOPADJUST;
      h1 = this.yScale(this.args.geometry.limits['ymin']) - y1 - RIGHTBOTTOMADJUST;

      const filterType = this.args.model.filterName;
      switch (filterType) {
        case FilterConstants.filterType.lowPass: {
          // Stopband Region is Bottom Right for Low Pass Filter
          x1 = this.xScale(this.args.model.specificationsInput.stopbandFrequency) + LEFTTOPADJUST;
          w1 = this.xScale(this.args.geometry.limits['xmax']) - x1 - RIGHTBOTTOMADJUST;
          break;
        }
        case FilterConstants.filterType.highPass: {
          // Stopband Region is Bottom Left for High Pass Filter
          x1 = this.xScale(this.args.geometry.limits['xmin']) + LEFTTOPADJUST;
          w1 = this.xScale(this.args.model.specificationsInput.stopbandFrequency) - x1 - RIGHTBOTTOMADJUST;
          break;
        }
        case FilterConstants.filterType.bandPass: {
          // Stopband Region is Bottom Left and Right for Band Pass Filter
          const bandpassStopBandFrequency = this.bandpassStopBandFrequency;
          const start1 = this.args.geometry.limits['xmin'];
          const stop1 = bandpassStopBandFrequency.stop;
          const start2 = bandpassStopBandFrequency.start;
          const stop2 = this.args.geometry.limits['xmax'];
          x1 = this.xScale(start1) + LEFTTOPADJUST;
          w1 = this.xScale(stop1) - x1 - RIGHTBOTTOMADJUST;
          x2 = this.xScale(start2) + LEFTTOPADJUST;
          y2 = y1;
          w2 = this.xScale(stop2) - x2 - RIGHTBOTTOMADJUST;
          h2 = h1;
          break;
        }
      }
    }
    return { x1: x1, y1: y1, w1: w1, h1: h1, x2: x2, y2: y2, w2: w2, h2: h2 };
  }

  // Stopband Region Text Position
  get stopbandRegionLabelPos(): StopbandRegionLabelPos {
    const area = this.stopbandRegion;
    const filterType = this.args.model.filterName;
    let x1 = 0,
      x2 = 0;
    const y1 = area.y1 + area.h1 - 5;
    const y2 = area.y2 + area.h2 - 5;
    let anchor = 'text-anchor-start';
    switch (filterType) {
      case FilterConstants.filterType.lowPass:
      case FilterConstants.filterType.highPass:
        // Stopband Region Text Position is Bottom Centered for Low/High Pass Filter
        x1 = x2 = area.x1 + area.w1 / 2;
        anchor = 'text-anchor-middle';
        break;
      case FilterConstants.filterType.bandPass:
        // Stopband Region Position is bottom left justified for the left region and  bottom right justified for the right region
        x1 = area.x1 + 2;
        x2 = area.x2 + area.w2 - 2;
        anchor = 'text-anchor-start';
        break;
    }
    return { x1: x1, y1: y1, anchor: anchor, x2: x2, y2: y2 };
  }

  // Stopband Arrows Position (only for Band Pass)
  get stopbandArrows(): ArrowPosition {
    const area = this.stopbandRegion;
    const x1 = area.x1 + area.w1;
    const y1 = area.y1 + 10;
    const x2 = area.x2;
    const y2 = area.y2 + 10;
    return { x1: x1, y1: y1, x2: x2, y2: y2 };
  }

  // Stopband Attenuation Text Position
  get stopbandAttenuationLabel(): StopbandAttenuationLabel {
    const area = this.stopbandRegion;
    const filterType = this.args.model.filterName;
    const value = Format.formatFieldValue(
      this.args.model.specificationsInput.stopbandAttenuation,
      FilterConstants.fieldTypes.NumericDecimal,
      '3s',
    );
    let x1 = 0,
      x2 = 0;
    const y1 = area.y1 - 5;
    const y2 = y1;
    let anchor = 'text-anchor-start';
    switch (filterType) {
      case FilterConstants.filterType.lowPass:
      case FilterConstants.filterType.highPass:
        // Attenuation Position is Above Stopband Region Centered for Low/High Pass Filter
        x1 = area.x1 + area.w1 / 2;
        anchor = 'text-anchor-middle';
        break;
      case FilterConstants.filterType.bandPass:
        // Attenuation Position is Above Stopband Region left justified for the left region and right justified for the right region
        x1 = area.x1 + 2;
        x2 = area.x2 + area.w2 - 2;
        anchor = 'text-anchor-start';
        break;
    }
    return { value: value, x1: x1, y1: y1, anchor: anchor, x2: x2, y2: y2 };
  }

  // Stopband Frequency Text Position
  get stopbandFrequencyLabel(): StopbandFrequencyLabel {
    const area = this.stopbandRegion;
    const filterType = this.args.model.filterName;
    const value = Format.formatFieldValue(
      this.args.model.specificationsInput.stopbandFrequency,
      FilterConstants.fieldTypes.Engineering,
      '3s',
    );
    let x = 0,
      y = 0;
    let anchor = 'text-anchor-start';
    switch (filterType) {
      case FilterConstants.filterType.lowPass:
        // Frequency Position is Left of Stopband Region for Low Pass Filter
        x = area.x1 - 5;
        y = area.y1 + area.h1 / 2;
        anchor = 'text-anchor-end';
        break;
      case FilterConstants.filterType.highPass:
        // Frequency Position is Right of Stopband Region for High Pass Filter
        x = area.x1 + area.w1 + 5;
        y = area.y1 + area.h1 / 2;
        anchor = 'text-anchor-start';
        break;
      case FilterConstants.filterType.bandPass:
        if (this.args.geometry.limits['xmin'] && this.args.geometry.limits['xmax']) {
          // Frequency Position is Center of Graph lined with the Regions for Band Pass Filter
          x = (this.xScale(this.args.geometry.limits['xmin']) + this.xScale(this.args.geometry.limits['xmax'])) / 2;
          y = area.y1 + 10;
          anchor = 'text-anchor-middle';
        }
        break;
    }
    return { value: value, x: x, y: y, anchor: anchor };
  }

  // Center Frequency Text Position
  get centerFrequencyLabel(): CenterFrequencyLabel {
    const filterType = this.args.model.filterName;
    const value = Format.formatFieldValue(
      this.args.model.specificationsInput.centerFrequency,
      FilterConstants.fieldTypes.Engineering,
      '3s',
    );
    let x = 0,
      y = 0;
    if (
      filterType === FilterConstants.filterType.bandPass &&
      this.args.geometry.limits['xmin'] &&
      this.args.geometry.limits['xmax']
    ) {
      x = (this.xScale(this.args.geometry.limits['xmin']) + this.xScale(this.args.geometry.limits['xmax'])) / 2;
      y = this.yScale(this.args.model.specificationsInput.passbandAttenuation + this.gainDB - 25);
    }
    return { value: value, x: x, y: y };
  }

  // Stopband Start and Stop Frequency Text (only for Band Pass)
  get stopbandStopStartFrequencyLabel(): StopbandStopStartFrequencyLabel {
    const area = this.stopbandRegion;
    const filterType = this.args.model.filterName;
    const bandpassStopBandFrequency = this.bandpassStopBandFrequency;
    const stop = Format.formatFieldValue(bandpassStopBandFrequency.stop, FilterConstants.fieldTypes.Engineering, '3s');
    const start = Format.formatFieldValue(
      bandpassStopBandFrequency.start,
      FilterConstants.fieldTypes.Engineering,
      '3s',
    );
    let x1 = 0,
      y1 = 0; // Stopband Region Area (1)
    let x2 = 0,
      y2 = 0; // Stopband Region Area (2)
    if (filterType === FilterConstants.filterType.bandPass) {
      x1 = area.x1 + area.w1 + 2;
      x2 = area.x2 - 2;
      y1 = y2 = area.y2 + area.h2 - 20;
    }
    return { stop: { value: stop, x: x1, y: y1 }, start: { value: start, x: x2, y: y2 } };
  }

  // Gain Value Text
  get gainValueLabel(): GainValueLabel {
    const area = this.passbandRegion;
    const filterType = this.args.model.filterName;
    const value = Format.formatFieldValue(this.gainDB, FilterConstants.fieldTypes.NumericDecimal, '3s');
    const x = filterType === FilterConstants.filterType.bandPass ? area.x : area.x + 15;
    const y = this.yScale(this.gainDB) - 5;
    const anchor = filterType === FilterConstants.filterType.bandPass ? 'text-anchor-end' : 'text-anchor-start';
    return { value: value, x: x, y: y, anchor: anchor };
  }

  // Passband Ripple (Only when Chebyshev)
  get passbandRipple(): PassbandRipple {
    let labelX = 0,
      labelY = 0; // This would be the position of the text label
    let anchor = 'text-anchor-start';
    let rtx1 = 0,
      rtx2 = 0,
      rty1 = 0,
      rty2 = 0; // Ripple Top Positions
    let rbx1 = 0,
      rbx2 = 0,
      rby1 = 0,
      rby2 = 0; // Ripple Bottom Positions
    const responseType = this.args.model.responseType;
    if (responseType === FilterConstants.responseType.chebyshev) {
      const gain = Number(this.gainDB);
      const area = this.passbandRegion;
      const order = Number(this.args.model.nthOrder);
      const responsevalue = Number(this.args.model.filterResponseValue);
      const filterType = this.args.model.filterName;
      switch (filterType) {
        case FilterConstants.filterType.lowPass:
        case FilterConstants.filterType.bandPass:
          // Ripple Label Position is Right of Passband Region for Low Pass and Band Pass Filter
          labelX = area.x + area.w + 5;
          labelY = this.yScale(gain);
          anchor = 'text-anchor-start';
          break;
        case FilterConstants.filterType.highPass:
          // Ripple Label Position is Left of Passband Region for High Pass Filter
          labelX = area.x - 5;
          labelY = this.yScale(gain);
          anchor = 'text-anchor-end';
          break;
      }
      // Top Ripple
      rty1 = rty2 = this.yScale(gain);
      rtx1 = area.x;
      rtx2 = area.x + area.w + LEFTTOPADJUST;
      // Bottom Ripple
      rby1 = rby2 = this.yScale(order % 2 === 0 ? gain + responsevalue : gain - responsevalue);
      rbx1 = area.x;
      rbx2 = area.x + area.w + LEFTTOPADJUST;
    }
    return {
      label: { x: labelX, y: labelY, anchor: anchor },
      rippleTop: { x1: rtx1, y1: rty1, x2: rtx2, y2: rty2 },
      rippleBottom: { x1: rbx1, y1: rby1, x2: rbx2, y2: rby2 },
    };
  }

  // Determines the Position of the Response Text based on the Filter Type and Graph Type
  // Returns: responsePosition - Values can either be "Left", "Center" or "Right"
  getResponsePosition(): string {
    const filterType = this.args.model.filterName;
    const figureName = this.args.figureName;
    let responsePosition = '';

    switch (figureName) {
      case FilterConstants.figureName.magnitudeDb:
      case FilterConstants.figureName.magnitudeVV:
        switch (filterType) {
          case FilterConstants.filterType.highPass:
            responsePosition = Position.RIGHT;
            break;
          case FilterConstants.filterType.bandPass:
            responsePosition = Position.CENTER;
            break;
          case FilterConstants.filterType.lowPass:
          default:
            responsePosition = Position.LEFT;
            break;
        }
        break;
      case FilterConstants.figureName.phaseDegrees:
      case FilterConstants.figureName.phaseRadians:
        responsePosition = Position.LEFT;
        break;
      case FilterConstants.figureName.phaseDelay:
      case FilterConstants.figureName.groupDelay:
        switch (filterType) {
          case FilterConstants.filterType.bandPass:
            responsePosition = Position.CENTER;
            break;
          case FilterConstants.filterType.highPass:
          case FilterConstants.filterType.lowPass:
          default:
            responsePosition = Position.LEFT;
            break;
        }
        break;
      case FilterConstants.figureName.stepResponse:
        responsePosition = Position.RIGHT;
        break;
    }

    return responsePosition;
  }

  // Computes for the x position based on the graph's X Scale
  // Returns: xPos - Converted Coordinate Position base on X Scale
  xScale(xPos: number): number {
    return this.args.geometry.xScale(xPos);
  }

  // Computes for the y position based on the graph's X Scale
  // Returns: xPos - Converted Coordinate Position base on X Scale
  yScale(yPos: number): number {
    return this.args.geometry.yScale(yPos);
  }
}
